import {
  TtOrderSnapshot,
  TtStagedEvent,
  TtOrderMetadata,
  TtOrderEvent,
  OrderEventType,
} from '@/lib/api/risk/brokerage/tt';
import userEvent from '@testing-library/user-event';

/**
 * Unified order row for display. Contains fields from the best available source
 * (snapshot preferred, then Added event, then Filled event, then other events).
 */
export interface TtOrderRow {
  guid_order_id: string;
  trading_account_name: string | null;
  instrument_name: string | null;
  order_quantity: string | null;
  text_a: string | null;
  text_b: string | null;
  text_c: string | null;
  text_tt: string | null;
  limit_price: string | null | undefined;
  user_name: string | null | undefined;

  /** Source of this row: 'snapshot' | 'added' | 'filled' | 'other' */
  source: 'snapshot' | 'added' | 'filled' | 'other';
  /** Whether this order is classified as applicable (HTA) */
  isApplicable: boolean;
  /** Whether this order has a specified customer ID */
  hasCustomerId: boolean;
  /** The associated metadata if available */
  metadata: TtOrderMetadata | null;
  exchange_event_time: string;
}

/**
 * Row type for fill events table display.
 */
export interface TtFillEventRow {
  id: string;
  guid_order_id: string;
  exchange_event_time: string | null;
  buy_sell_type: string | null;
  order_quantity: string | null;
  fill_quantity: string | null;
  avg_fill_price: string | null;
  message: string | null;
  instrument_name: string | null;
  text_a: string | null;
  text_b: string | null;
  text_c: string | null;
  text_tt: string | null;
  user_name: string | null;
  /** The associated metadata if available */
  metadata: TtOrderMetadata | null;
  /** Whether this fill event is HTA */
  isHta: boolean;
  /** Whether this fill event has a customer assigned */
  hasCustomer: boolean;
}

export type TtOrderFilter = 'missing_applicable' | 'missing_other' | 'staged_hta';

export type TtFillEventsFilter = 'all' | 'missing_customer' | 'has_customer';

export interface TtOrderViewResult {
  /** Orders missing customer ID, classified as applicable (HTA) */
  missingApplicable: TtOrderRow[];
  /** Orders missing customer ID, classified as other (non-HTA) */
  missingOther: TtOrderRow[];
  /** Orders that are staged and HTA with a customer ID (will be processed) */
  stagedHta: TtOrderRow[];
  /** All distinct orders for reference */
  allOrders: TtOrderRow[];
}

export interface TtSummaryStats {
  totalMissingApplicable: number;
  totalMissingOther: number;
  totalStagedHta: number;
  /** Breakdown by account for the currently active filter */
  byAccount: Array<{ account: string; count: number }>;
}

interface BuildTtOrderViewInput {
  snapshots: TtOrderSnapshot[];
  stagedEvents: TtStagedEvent[];
  metadataByGuid: Map<string, TtOrderMetadata>;
}

/**
 * Check if text fields contain "hta" (case-insensitive).
 */
function textContainsHta(
  text_a: string | null,
  text_b: string | null,
  text_c: string | null,
  text_tt: string | null
): boolean {
  const fields = [text_a, text_b, text_c, text_tt];
  return fields.some((f) => /hta/i.test(f ?? ""));
}

function textContainsPricing(
  text_a: string | null,
  text_b: string | null,
  text_c: string | null,
  text_tt: string | null
): boolean {
  const fields = [text_a, text_b, text_c, text_tt];
  return fields.some((f) => /pricing/i.test(f ?? ""));
}

function textContainsAdj(
  text_a: string | null,
  text_b: string | null,
  text_c: string | null,
  text_tt: string | null
): boolean {
  const fields = [text_a, text_b, text_c, text_tt];
  return fields.some((f) => /pos/i.test(f ?? "") && /adj/i.test(f ?? ""));
}

export type GuessedOrderType = 'hta' | 'pricing' | 'position_adjust' | null;

/**
 * Guess order type from text fields using regex patterns.
 * Priority: HTA > Pricing > Position Adjust > null
 */
export function guessOrderTypeFromText(
  text_a: string | null,
  text_b: string | null,
  text_c: string | null,
  text_tt: string | null
): GuessedOrderType {
  if (textContainsHta(text_a, text_b, text_c, text_tt)) return 'hta';
  if (textContainsPricing(text_a, text_b, text_c, text_tt)) return 'pricing';
  if (textContainsAdj(text_a, text_b, text_c, text_tt)) return 'position_adjust';
  return null;
}

/**
 * Determine if an order is applicable (HTA) based on metadata or text heuristics.
 */
export function isApplicableHta(
  metadata: TtOrderMetadata | null | undefined,
  text_a: string | null,
  text_b: string | null,
  text_c: string | null,
  text_tt: string | null
): boolean {
  if (metadata) {
    return metadata.expected_hta;
  }
  // Fallback: check text fields for "hta"
  return textContainsHta(text_a, text_b, text_c, text_tt);
}

/**
 * Priority for event types when selecting the best source.
 * Lower number = higher priority.
 */
function eventTypePriority(eventType: string | null): number {
  if (eventType === OrderEventType.Added) return 1;
  if (eventType === OrderEventType.Filled) return 2;
  return 3; // Other events grouped together
}

/**
 * Select the preferred staged event for a given guid_order_id.
 * Priority: Added > Filled > other
 */
function selectPreferredStagedEvent(
  events: TtStagedEvent[]
): TtStagedEvent | null {
  if (events.length === 0) return null;
  return events.reduce((best, current) => {
    const bestPriority = eventTypePriority(best.event_type);
    const currentPriority = eventTypePriority(current.event_type);
    return currentPriority < bestPriority ? current : best;
  });
}

/**
 * Build a unified view of TT orders from snapshots and staged events.
 */
export function buildTtOrderView({
  snapshots,
  stagedEvents,
  metadataByGuid,
}: BuildTtOrderViewInput): TtOrderViewResult {
  const orderMap = new Map<string, TtOrderRow>();

  // Group staged events by guid_order_id
  const stagedByGuid = new Map<string, TtStagedEvent[]>();
  for (const event of stagedEvents) {
    if (!event.guid_order_id) continue;
    const existing = stagedByGuid.get(event.guid_order_id) ?? [];
    existing.push(event);
    stagedByGuid.set(event.guid_order_id, existing);
  }

  // Process snapshots first (highest priority source)
  for (const snapshot of snapshots) {
    if (!snapshot.guid_order_id) continue;

    const guidOrderId = snapshot.guid_order_id;
    const metadata = metadataByGuid.get(guidOrderId) ?? null;
    const hasCustomerId = Boolean(metadata?.specified_customer_id);

    const applicable = true;

    orderMap.set(guidOrderId, {
      guid_order_id: guidOrderId,
      trading_account_name: snapshot.trading_account_name,
      instrument_name: snapshot.instrument_name,
      order_quantity: snapshot.quantity,
      text_a: snapshot.text_a,
      text_b: snapshot.text_b,
      text_c: snapshot.text_c,
      text_tt: snapshot.text_tt,
      source: 'snapshot',
      isApplicable: applicable,
      hasCustomerId,
      exchange_event_time: snapshot.exchange_event_time,
      metadata,
      limit_price: snapshot.limit_price,
      user_name: snapshot.user_name,
    });
  }

  // Process staged events for guid_order_ids not already covered by snapshots
  for (const [guidOrderId, events] of stagedByGuid) {
    if (orderMap.has(guidOrderId)) continue;

    const preferredEvent = selectPreferredStagedEvent(events);
    if (!preferredEvent) continue;

    const orderEvent = preferredEvent.order_event;
    const metadata = metadataByGuid.get(guidOrderId) ?? null;
    const hasCustomerId = Boolean(metadata?.specified_customer_id);

    // Determine source type
    let source: 'added' | 'filled' | 'other' = 'other';
    if (preferredEvent.event_type === OrderEventType.Added) {
      source = 'added';
    } else if (preferredEvent.event_type === OrderEventType.Filled) {
      source = 'filled';
    }

    // Get text fields from order_event if available
    const text_a = orderEvent?.text_a ?? null;
    const text_b = orderEvent?.text_b ?? null;
    const text_c = orderEvent?.text_c ?? null;
    const text_tt = orderEvent?.text_tt ?? null;

    // const applicable = isApplicableHta(metadata, text_a, text_b, text_c, text_tt);

    orderMap.set(guidOrderId, {
      guid_order_id: guidOrderId,
      trading_account_name: orderEvent?.trading_account_name ?? null,
      instrument_name: orderEvent?.instrument_name ?? null,
      order_quantity: orderEvent?.order_quantity ?? orderEvent?.quantity ?? null,
      text_a,
      text_b,
      text_c,
      text_tt,
      source,
      isApplicable: true,
      hasCustomerId,
      metadata,
      exchange_event_time: orderEvent?.exchange_event_time!,
      limit_price: orderEvent?.limit_price,
      user_name: orderEvent?.user_name,
    });
  }

  // Categorize orders
  const allOrders = Array.from(orderMap.values());
  const missingApplicable: TtOrderRow[] = [];
  const missingOther: TtOrderRow[] = [];
  const stagedHta: TtOrderRow[] = [];

  for (const order of allOrders) {
    if (!order.hasCustomerId) {
      // Missing customer ID
      if (order.isApplicable) {
        missingApplicable.push(order);
      } else {
        missingOther.push(order);
      }
    }
  }

  return {
    missingApplicable,
    missingOther,
    stagedHta,
    allOrders,
  };
}

/**
 * Compute summary statistics for the given order view and active filter.
 */
export function computeSummaryStats(
  view: TtOrderViewResult,
  activeFilter: TtOrderFilter
): TtSummaryStats {
  // Get the list for the active filter
  let activeList: TtOrderRow[];
  switch (activeFilter) {
    case 'missing_applicable':
      activeList = view.missingApplicable;
      break;
    case 'missing_other':
      activeList = view.missingOther;
      break;
    case 'staged_hta':
      activeList = view.stagedHta;
      break;
  }

  // Compute by-account breakdown for the active filter
  const accountCounts: Record<string, number> = {};
  for (const order of activeList) {
    const account = order.trading_account_name ?? 'Unknown';
    accountCounts[account] = (accountCounts[account] ?? 0) + 1;
  }

  const byAccount = Object.entries(accountCounts)
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([account, count]) => ({ account, count }));

  return {
    totalMissingApplicable: view.missingApplicable.length,
    totalMissingOther: view.missingOther.length,
    totalStagedHta: view.stagedHta.length,
    byAccount,
  };
}

/**
 * Get the filtered list of orders based on the active filter.
 */
export function getFilteredOrders(
  view: TtOrderViewResult,
  activeFilter: TtOrderFilter
): TtOrderRow[] {
  switch (activeFilter) {
    case 'missing_applicable':
      return view.missingApplicable;
    case 'missing_other':
      return view.missingOther;
    case 'staged_hta':
      return view.stagedHta;
  }
}

/**
 * Build a metadata map from an array of metadata objects.
 */
export function buildMetadataMap(
  metadataList: TtOrderMetadata[]
): Map<string, TtOrderMetadata> {
  const map = new Map<string, TtOrderMetadata>();

  for (const m of metadataList) {
    map.set(m.guid_order_id, m);
  }

  return map;
}

/**
 * Build fill event rows from staged events, filtering to only Filled event types.
 */
export interface BuildFillEventsInput {
  stagedEvents: TtStagedEvent[];
  metadataByGuid: Map<string, TtOrderMetadata>;
}

export function buildFillEventsView({
  stagedEvents,
  metadataByGuid,
}: BuildFillEventsInput): TtFillEventRow[] {
  const fillEvents: TtFillEventRow[] = [];

  for (const staged of stagedEvents) {
    // Only include Filled events
    if (staged.event_type !== OrderEventType.Filled) continue;

    const orderEvent = staged.order_event;
    if (!orderEvent) continue;

    const guidOrderId = staged.guid_order_id ?? orderEvent.guid_order_id;
    if (!guidOrderId) continue;

    const metadata = metadataByGuid.get(guidOrderId) ?? staged.meta_data ?? null;
    const hasCustomer = Boolean(metadata?.specified_customer_id);

    // Check if HTA using metadata or text heuristics
    const isHta = isApplicableHta(
      metadata,
      orderEvent.text_a,
      orderEvent.text_b,
      orderEvent.text_c,
      orderEvent.text_tt
    );

    // Only include HTA fill events
    if (!isHta) continue;

    fillEvents.push({
      id: staged.id,
      guid_order_id: guidOrderId,
      exchange_event_time: orderEvent.exchange_event_time,
      buy_sell_type: orderEvent.buy_sell_type,
      order_quantity: orderEvent.order_quantity ?? orderEvent.quantity,
      fill_quantity: orderEvent.fill_quantity ?? orderEvent.filled_quantity,
      avg_fill_price: orderEvent.avg_fill_price,
      message: orderEvent.message,
      instrument_name: orderEvent.instrument_name,
      text_a: orderEvent.text_a,
      text_b: orderEvent.text_b,
      text_c: orderEvent.text_c,
      text_tt: orderEvent.text_tt,
      user_name: orderEvent.user_name,
      metadata,
      isHta,
      hasCustomer,
    });
  }

  // Sort by exchange_event_time descending (most recent first)
  fillEvents.sort((a, b) => {
    const timeA = a.exchange_event_time ?? '';
    const timeB = b.exchange_event_time ?? '';
    return timeB.localeCompare(timeA);
  });

  return fillEvents;
}

/**
 * Get filtered fill events based on the active filter.
 */
export function getFilteredFillEvents(
  fillEvents: TtFillEventRow[],
  filter: TtFillEventsFilter
): TtFillEventRow[] {
  switch (filter) {
    case 'all':
      return fillEvents;
    case 'missing_customer':
      return fillEvents.filter((e) => !e.hasCustomer);
    case 'has_customer':
      return fillEvents.filter((e) => e.hasCustomer);
  }
}
